import React, { useEffect, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import DataTable from "@/components/shared/DataTable";
import SeverityBadge from "@/components/shared/SeverityBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import api, { extractApiError } from "@/lib/api";
import { UserCheck, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { downloadReport } from "@/lib/downloadReport";
import { formatDisplayId } from "@/lib/utils";

const DOCTORS = ["Dr. Mehta", "Dr. Verma", "Dr. Patel", "Dr. Iyer", "Dr. Khan"];

export default function HospitalActiveCases() {
  const [open, setOpen] = useState(null);
  const [doctor, setDoctor] = useState(DOCTORS[0]);
  const [bed, setBed] = useState("");
  const [list, setList] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadPatients = async () => {
      setError("");
      try {
        const response = await api.get("/patients");
        const patientsData = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];

        if (!mounted) return;

        setList(
          patientsData.filter((patient) => {
            const severity = String(patient?.severity || "").toLowerCase();
            const status = String(patient?.status || "").toLowerCase();
            return severity !== "low" && (status === "incoming" || status === "active");
          }),
        );
      } catch (err) {
        if (!mounted) return;
        const { message } = extractApiError(err);
        setError(message || "Failed to load active cases.");
        setList([]);
      }
    };

    loadPatients();

    return () => {
      mounted = false;
    };
  }, []);

  const startTreatment = () => {
    setOpen(null);
    toast.success(`${open.name} assigned to ${doctor} · Bed ${bed || "auto"}`);
  };

  return (
    <div className="space-y-6" data-testid="hospital-active">
      <PageHeader title="Active Cases" subtitle="Patients currently under treatment." onDownload={() => {
        downloadReport({
          filename: "hospital_active_cases",
          title: "Hospital - Active Cases Report",
          headers: ["ID", "Name", "Severity", "Vitals", "Needs", "Doctor"],
          rows: list.map((p) => [p.id, p.name, p.severity, p.vitals, p.needs, p.doctor || "Unassigned"]),
        });
        toast.success("Report downloaded");
      }} />
      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}
      <SectionCard padded={false}>
        <DataTable
          data={list}
          getRowProps={(p) => ({ "data-testid": `active-${p.id}` })}
          columns={[
            {
              key: "patient", label: "Patient",
              render: (p) => (
                <div>
                  <p className="font-semibold text-slate-900">{p.name}</p>
                  <p className="font-mono text-[10px] text-slate-400">{formatDisplayId(p.id)} · {p.incident}</p>
                </div>
              ),
            },
            { key: "severity", label: "Severity", render: (p) => <SeverityBadge severity={p.severity} /> },
            { key: "vitals", label: "Vitals", cellClassName: "text-xs text-slate-700" },
            { key: "needs", label: "Needs", cellClassName: "text-slate-700" },
            { key: "doctor", label: "Doctor", cellClassName: "text-slate-700", render: (p) => p.doctor || "—" },
            {
              key: "actions", label: "Actions", className: "text-right", cellClassName: "text-right",
              render: (p) => (
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setOpen(p)} data-testid={`assign-${p.id}`}>
                    <UserCheck size={13} className="mr-1" /> Assign
                  </Button>
                  <Button
                    size="sm"
                    className="bg-[hsl(var(--role-primary))] text-white hover:bg-[hsl(var(--role-primary))]/90"
                    onClick={() => toast.success(`Started treatment for ${p.name}`)}
                    data-testid={`treat-${p.id}`}
                  >
                    <Stethoscope size={13} className="mr-1" /> Treat
                  </Button>
                </div>
              ),
            },
          ]}
        />
      </SectionCard>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="sm:max-w-md">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Assign care · {open.name}</DialogTitle>
                <DialogDescription>Pick doctor and bed to start treatment.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Doctor</Label>
                  <Select value={doctor} onValueChange={setDoctor}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DOCTORS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Bed (optional)</Label>
                  <Input value={bed} onChange={(e) => setBed(e.target.value)} placeholder="e.g. ICU-04" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(null)}>Cancel</Button>
                <Button className="bg-[hsl(var(--role-primary))] text-white hover:bg-[hsl(var(--role-primary))]/90" onClick={startTreatment}>
                  Start treatment
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
